import { matchPath, useLocation } from 'react-router-dom';
import { LoadRemote } from '@mindtickle/ui-shell-core/DynamicRemote';

import routes from '~/components/Routes/config/routesConfig';
import { ROUTES_NAMESPACE, REMOTE_FILE } from '~/config/app.constants';
import ErrorBoundary from '~/components/Tools/ErrorBoundary';

type WithOtherRemotesProps = {
  children: React.ReactNode;
};

export const withOtherRemotes = <P extends object>(WrappedComponent: React.ComponentType<P>) => (
  props: P & WithOtherRemotesProps
) => {
  const location = useLocation();

  //eslint-disable-next-line complexity
  const getMatchedRouteWithOtherRemote = () => {
    const allRoutes = [
      ...(routes?.protectedRoutes || []),
      ...(routes?.publicRoutes || []),
      ...(routes?.shareableRoutes || []),
    ];
    for (let i = 0; i < allRoutes.length; i++) {
      const route = allRoutes[i];
      const routePath = route?.path;
      if (!routePath) continue;
      const pattern = `/${ROUTES_NAMESPACE.map(ns => ns.replace('*', routePath)).join('|')}`;
      if (matchPath(pattern, location.pathname) && route?.otherRemotesConfig) {
        //eslint-disable-next-line max-depth
        if (
          route.otherRemotesConfig.blacklistedRoutes?.some((blacklistedRoute: RegExp) =>
            blacklistedRoute.test(location.pathname)
          )
        ) {
          continue; // Skip this route if pathname matches blacklisted routes
        }

        return route;
      }
    }
    return null;
  };

  const matchedRoute = getMatchedRouteWithOtherRemote();
  const { children, ...restProps } = props;

  const enhancedChildren = (
    <>
      {children}
      {matchedRoute && matchedRoute.otherRemotesConfig && (
        <div className="other-remotes-config">
          <ErrorBoundary fallback={<></>}>
            <LoadRemote
              remote={{
                url: `${matchedRoute.otherRemotesConfig.host}/${REMOTE_FILE}`,
                moduleName: matchedRoute.otherRemotesConfig.moduleName,
                remoteName: matchedRoute.otherRemotesConfig.remoteName,
              }}
              loader={<></>}
              lazy={false}
              onIdleEnabled={false}
            />
          </ErrorBoundary>
        </div>
      )}
    </>
  );

  return <WrappedComponent {...(restProps as P)}>{enhancedChildren}</WrappedComponent>;
};
